import BaseCashoutService from './base-cashout-service.js';
import TaskRunner from 'services/common/task-runner/task-runner.js';
import PollCashoutOffersTask from 'services/bets/task/poll-cashout-offers-task.js';
import BetsTypes from 'services/bets/types.js';

const { POLL } = BetsTypes.CASHOUT_PROTOCOLS;

const SECOND = 1000;

/**
 * Default interval in ms used to poll cashout offers.
 *
 * @typedef DEFAULT_POLL_CASHOUT_INTERVAL
 * @type {number}
 * @memberof Mojito.Services.Bets.pollCashoutService
 */
const DEFAULT_POLL_CASHOUT_INTERVAL = 30 * SECOND;

/**
 * Default bets count per request.
 *
 * @typedef DEFAULT_BETS_PER_REQUEST
 * @type {number}
 * @memberof Mojito.Services.Bets.pollCashoutService
 */
const DEFAULT_BETS_PER_REQUEST = 50;

/**
 * Cashout service config.
 *
 * @typedef {Mojito.Services.Bets.cashoutService.CashoutServiceConfig} PollCashoutServiceConfig
 * @property {number} [pollIntervalSec = 10] - Interval in seconds to poll values needed for tickets early cashout. Polling will be disabled if set to 0.
 * @property {number} [maxBetsPerRequest = 50] - Maximum number of bets to include in cashout value request. Setting to 0 will add all bets in request.
 *
 * @memberof Mojito.Services.Bets.pollCashoutService
 */

/**
 * Class offering the possibility to interact with the mojito bets cashout API using
 * {@link Mojito.Services.Bets.types.CASHOUT_PROTOCOLS.POLL|POLL} transport to retrieve cash out offers.
 *
 * @class PollCashoutService
 * @name pollCashoutService
 * @extends Mojito.Services.Bets.BaseCashoutService
 * @memberof Mojito.Services.Bets
 */
class PollCashoutService extends BaseCashoutService {
    constructor() {
        super(POLL);
    }

    /**
     * Configure service.
     *
     * @param {Mojito.Services.Bets.pollCashoutService.PollCashoutServiceConfig} config - Service configuration object.
     *
     * @function Mojito.Services.Bets.pollCashoutService#configure
     */
    configure({ serviceUrl, pollIntervalSec, maxBetsPerRequest }) {
        super.configure({ serviceUrl });
        const interval = !isNaN(pollIntervalSec)
            ? pollIntervalSec * SECOND
            : DEFAULT_POLL_CASHOUT_INTERVAL;
        this.valuesPoller = interval && new TaskRunner(interval);
        this.maxBetsPerRequest = !isNaN(maxBetsPerRequest)
            ? maxBetsPerRequest
            : DEFAULT_BETS_PER_REQUEST;
    }

    /**
     * Requests cashout offers for bets.
     * This method starts running {@link Mojito.Services.Bets.Task.PollCashoutOffersTask|PollCashoutOffersTask}.
     * If task is already in the middle of execution, it will be reset and started again with newly provided arguments.
     * If <code>pollIntervalSec</code> config property has been set to 0 then
     * {@link Mojito.Services.Bets.cashoutService#getCashoutOffers|getCashoutOffers} will be called.
     *
     * @param {Array<Mojito.Services.Bets.types.BetInfo>} betInfos - List of bet info objects.
     *
     * @param {Mojito.Services.Bets.types.cashoutOffersSuccess} success - Callback executed once cashout offers successfully fetched.
     * If polling enabled - will be passed into {@link Mojito.Services.Bets.Task.PollCashoutOffersTask|PollCashoutOffersTask} constructor as <code>chunkSuccess</code> parameter.
     *
     * @param {Mojito.Services.Bets.types.cashoutValuesFail} fail - Callback executed once cashout offers failed to fetch.
     * If polling enabled - will be passed into {@link Mojito.Services.Bets.Task.PollCashoutOffersTask|PollCashoutOffersTask} constructor as <code>chunkFail</code> parameter.
     *
     * @function Mojito.Services.Bets.pollCashoutService#requestCashoutOffers
     */
    requestCashoutOffers(betInfos, success, fail) {
        this.validateCashoutTransport(POLL)
            .then(() => {
                if (this.valuesPoller) {
                    if (this.valuesPoller.isExecuting) {
                        this.valuesPoller.reset();
                    }
                    const config = {
                        service: this,
                        betInfos,
                        maxBetsPerRequest: this.maxBetsPerRequest,
                    };
                    const task = new PollCashoutOffersTask(config, success, fail);
                    this.valuesPoller.run(task);
                } else {
                    this.getCashoutOffers(betInfos).then(success).catch(fail);
                }
            })
            .catch(fail);
    }

    /**
     * Discard cash out values request made by {@link Mojito.Services.Bets.cashoutService#requestCashoutOffers|requestCashoutOffers}.
     * Stops {@link Mojito.Services.Bets.Task.PollCashoutOffersTask|PollCashoutOffersTask}.
     *
     * @function Mojito.Services.Bets.pollCashoutService#discardCashoutOffers
     */
    discardCashoutOffers() {
        this.invalidateCashoutTransport();
        if (this.valuesPoller) {
            this.valuesPoller.reset();
        }
    }

    /**
     * Requests cashout offers for bets.
     *
     * @param {Array<Mojito.Services.Bets.types.BetInfo>} betInfos - List of bet info objects.
     * @returns {Promise} Promise object. Resolves on get cashout offers response.
     * @function Mojito.Services.Bets.pollCashoutService#getCashoutOffers
     */
    getCashoutOffers(betInfos) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(`${this.apiUrl}/get-cashout-values`)
            .withDescription('get cashout offers')
            .withCredentials()
            .send({ betInfos });
    }

    /**
     * Request a single cashout offer for one bet.
     *
     * @param {Mojito.Services.Bets.types.BetInfo} betInfo - Bet info object.
     * @returns {Promise<Mojito.Services.Bets.types.CashoutInfo|undefined>} Promise resolving with the cashout offer or undefined.
     * @function Mojito.Services.Bets.pollCashoutService#requestCashoutOffer
     */
    requestCashoutOffer(betInfo) {
        return this.getCashoutOffers([betInfo]).then(response => response?.cashoutInfos?.[0]);
    }

    cashout(betInfo, amount) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(`${this.apiUrl}/cashout`)
            .withDescription('cashout bet')
            .withCredentials()
            .send({ betInfo, amount });
    }

    addAutoCashoutRule(betInfo, amount) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(`${this.apiUrl}/add-auto-cashout-rule`)
            .withDescription('add auto cashout rule')
            .withCredentials()
            .send({ betInfo, amount });
    }

    removeAutoCashoutRule(betInfo) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        return this.requestFactory
            .post(`${this.apiUrl}/remove-auto-cashout-rule`)
            .withDescription('remove auto cashout rule')
            .withCredentials()
            .send({ betInfo });
    }

    /**
     * Validate service url.
     *
     * @private
     * @returns {boolean} True if service url exists, else false.
     */
    validateServiceUrl() {
        if (!this.serviceUrl) {
            this.logger.warn('serviceUrl is missing.');
            return false;
        }
        return true;
    }
}

export default new PollCashoutService();
